import { FORECAST_ROUND_THRESHOLD, roundHoursWithThreshold, roundQuantityWithThreshold } from './forecastMath.js';

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_MAPPING = {
  lunes: 'lunes',
  martes: 'martes',
  miércoles: 'miercoles',
  miercoles: 'miercoles',
  jueves: 'jueves',
  viernes: 'viernes',
  sábado: 'sabado',
  sabado: 'sabado',
  domingo: 'domingo'
};

const clampHoursOverride = (value, maxHours = 12) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return Math.max(1, Math.min(maxHours, value));
};

const sanitizeDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value);
  }
  return new Date();
};

const sanitizeModifiers = (modificadores = {}) => ({
  clima: Number(modificadores.clima) || 0,
  promo: Number(modificadores.promo) || 0,
  evento: Number(modificadores.evento) || 0
});

const isRecipeProcess = (selectedArticulos = []) =>
  selectedArticulos.some((articulo) => articulo?.proceso_nombre || articulo?.articulo_nombre);

const buildCategoryKey = (articulo, recipeProcess) => {
  if (!articulo) {
    return 'category_Sin categoría';
  }
  return recipeProcess
    ? `linked_${articulo.articulo_nombre || 'Sin artículo vinculado'}`
    : `category_${articulo.categoria || 'Sin categoría'}`;
};

const getArticleData = (selectedArticulos = [], articleName) =>
  selectedArticulos.find((item) => item.articulo === articleName) || null;

const normalizeText = (value) => {
  if (!value) {
    return '';
  }
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const getAzucarGlassBaseContext = (selectedArticulos = [], forecastAverages = []) => {
  let baseArticle = selectedArticulos.find((articulo) => (
    articulo?.es_base_azucar_glass || articulo?.es_base
  ));
  if (!baseArticle) {
    const azucarCandidates = selectedArticulos.filter((articulo) => articulo?.proceso_es_azucar_glass);
    const byName = azucarCandidates.find((articulo) => (
      !articulo?.usar_equivalencia && normalizeText(articulo?.articulo).includes('azucar glass')
    ));
    baseArticle = byName || azucarCandidates.find((articulo) => !articulo?.usar_equivalencia) || null;
  }
  if (!baseArticle) {
    return null;
  }
  const baseName = baseArticle.articulo;
  if (!baseName) {
    return null;
  }
  const columnKey = detectColumnKey(baseArticle, baseName, forecastAverages);
  const categoryKey = buildCategoryKey(baseArticle, isRecipeProcess(selectedArticulos));
  return { baseArticle, columnKey, categoryKey };
};

const detectColumnKey = (articleData, articleName, forecastAverages = []) => {
  if (!forecastAverages.length || !articleData) {
    return articleName;
  }
  const baseRow = forecastAverages[0];
  const availableColumns = Object.keys(baseRow || {});
  const isRecipe = Boolean(
    articleData.receta_nombre ||
    articleData.proceso_nombre ||
    articleData.plu1 ||
    articleData.nombre
  );
  if (!isRecipe) {
    return articleName;
  }
  const candidates = [articleName, articleData.receta_nombre, articleData.nombre].filter(Boolean);
  const match = candidates.find((key) => availableColumns.includes(key));
  return match || articleName;
};

const getWindowMinutes = (articleData, hoursOverride) => {
  const vidaUtilMinutos = articleData?.minutos || 1440;
  const maxHours = articleData?.proceso_es_azucar_glass ? 24 : 12;
  const override = clampHoursOverride(hoursOverride, maxHours);
  if (override === null) {
    return vidaUtilMinutos;
  }
  return Math.min(vidaUtilMinutos, override * 60);
};

const getStockValue = (article, stockOverrides = {}) => {
  if (!article) {
    return 0;
  }
  const override = stockOverrides[article.articulo];
  if (override !== undefined) {
    const parsed = parseInt(override, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
    return 0;
  }
  const fallback = parseInt(article.stock_capturado, 10);
  if (Number.isFinite(fallback) && fallback > 0) {
    return fallback;
  }
  return 0;
};

const formatCoverageTimestamp = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}:${pad(dateObj.getSeconds())}`;
};

export const applyModifiers = (value, modificadores) => {
  const safe = sanitizeModifiers(modificadores);
  return value *
    (1 + safe.clima / 100) *
    (1 + safe.promo / 100) *
    (1 + safe.evento / 100);
};

export const applyCategoryAdjustment = (value, categoryKey, adjustments = {}) => {
  const adjustment = adjustments[categoryKey];
  if (!adjustment || typeof adjustment.percentage !== 'number' || adjustment.percentage === 0) {
    return value;
  }
  return value * (1 + adjustment.percentage / 100);
};

const resolveAzucarGlassEquivalence = (articleData) => {
  if (!articleData?.proceso_es_azucar_glass || !articleData?.usar_equivalencia) {
    return null;
  }
  const parsed = Number(articleData.equivalencia);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

const roundAzucarValue = (value) => (Number.isFinite(value) ? Number(value.toFixed(2)) : 0);

const splitAzucarGlassRecipes = (value, maxPerRecipe = 5) => {
  if (!Number.isFinite(value) || value <= 0) {
    return [];
  }
  const batches = [];
  let remaining = roundAzucarValue(value);
  let safety = 0;
  while (remaining > 0 && safety < 20) {
    const chunk = Math.min(maxPerRecipe, remaining);
    const roundedChunk = roundAzucarValue(chunk);
    if (roundedChunk <= 0) {
      break;
    }
    batches.push(roundedChunk);
    remaining = roundAzucarValue(remaining - roundedChunk);
    safety += 1;
  }
  return batches;
};

const buildAzucarGlassIngredients = ({ selectedArticulos, baseArticle, recipeAmount }) => {
  if (!Array.isArray(selectedArticulos) || !baseArticle || !Number.isFinite(recipeAmount)) {
    return [];
  }
  return selectedArticulos
    .filter((articulo) => articulo && articulo !== baseArticle && resolveAzucarGlassEquivalence(articulo))
    .map((articulo) => {
      const equivalence = resolveAzucarGlassEquivalence(articulo);
      if (!equivalence) {
        return null;
      }
      const quantity = roundAzucarValue(recipeAmount * equivalence);
      if (quantity <= 0) {
        return null;
      }
      const unitLabel = (articulo.unidades || articulo.unidad || 'UNIDADES').toString().toUpperCase();
      return {
        name: articulo.articulo,
        quantity,
        displayQuantity: quantity.toFixed(2),
        unitLabel
      };
    })
    .filter(Boolean);
};

const convertForecastUnits = (articleData, hourlyValue) => {
  const portion = articleData?.porcion || 1;
  return hourlyValue / portion;
};

const resolveAzucarGlassUnits = ({ articleData, hourlyValue, baseContext }) => {
  const equivalence = resolveAzucarGlassEquivalence(articleData);
  if (equivalence && baseContext?.baseArticle) {
    const baseUnits = convertForecastUnits(baseContext.baseArticle, hourlyValue);
    return baseUnits * equivalence;
  }
  return convertForecastUnits(articleData, hourlyValue);
};

const resolveHourlyValueRaw = ({ hourRow, articleData, columnKey, baseContext }) => {
  if (!hourRow) {
    return null;
  }
  let resolvedKey = columnKey;
  if (articleData?.proceso_es_azucar_glass && articleData?.usar_equivalencia && baseContext?.columnKey) {
    resolvedKey = baseContext.columnKey;
  }
  if (hourRow[resolvedKey] === undefined) {
    return null;
  }
  return parseFloat(hourRow[resolvedKey]) || 0;
};

const resolveHourlyValueAdjusted = ({
  hourRow,
  articleData,
  columnKey,
  categoryKey,
  modifiers,
  categoryAdjustments,
  baseContext
}) => {
  if (!hourRow) {
    return null;
  }
  let resolvedKey = columnKey;
  let resolvedCategoryKey = categoryKey;
  if (articleData?.proceso_es_azucar_glass && articleData?.usar_equivalencia && baseContext?.columnKey) {
    resolvedKey = baseContext.columnKey;
    resolvedCategoryKey = baseContext.categoryKey;
  }
  if (hourRow[resolvedKey] === undefined) {
    return null;
  }
  let hourlyValue = parseFloat(hourRow[resolvedKey]) || 0;
  hourlyValue = applyModifiers(hourlyValue, modifiers);
  hourlyValue = applyCategoryAdjustment(hourlyValue, resolvedCategoryKey, categoryAdjustments);
  return hourlyValue;
};

const isArticleAvailableAtTime = (articleData, targetTime) => {
  if (!articleData || !Array.isArray(articleData.horarios_operacion)) {
    return true;
  }
  const targetDay = targetTime.toLocaleDateString('es-ES', { weekday: 'long' }).toLowerCase();
  const scheduleForDay = articleData.horarios_operacion.find((schedule) => schedule.dia_semana === DAY_MAPPING[targetDay]);
  if (!scheduleForDay) {
    return true;
  }
  if (scheduleForDay.es_24_horas) {
    return true;
  }
  const startMinutes = scheduleForDay.hora_inicio_minutos || 0;
  const endMinutes = scheduleForDay.hora_fin_minutos ?? 1439;
  const currentMinutes = targetTime.getHours() * 60 + targetTime.getMinutes();
  if (startMinutes > endMinutes) {
    // Overnight schedule
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }
  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
};

export const calculateArticleTotal = ({
  articleName,
  forecastAverages,
  selectedArticulos,
  hoursOverride,
  simulationTime
}) => {
  if (!forecastAverages?.length || !articleName) {
    return 0;
  }
  const articleData = getArticleData(selectedArticulos, articleName);
  const windowMinutes = getWindowMinutes(articleData, hoursOverride);
  const start = sanitizeDate(simulationTime);
  const horizonEnd = new Date(start.getTime() + windowMinutes * 60000);
  const columnKey = detectColumnKey(articleData, articleName, forecastAverages);
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastAverages);

  let usedMinutes = 0;
  let total = 0;
  let cursor = new Date(start);
  let startedSegment = false;

  while (usedMinutes < windowMinutes && cursor < horizonEnd) {
    const minutesToHourEnd = 60 - cursor.getMinutes();
    const remaining = Math.max(0, Math.ceil((horizonEnd - cursor) / 60000));
    const step = Math.min(15, minutesToHourEnd, windowMinutes - usedMinutes, remaining);

    if (isArticleAvailableAtTime(articleData, cursor)) {
      startedSegment = true;
      const dayName = DAYS[cursor.getDay()];
      const hourRow = forecastAverages.find((row) => row.Dia === dayName && row.Hora === cursor.getHours());
      const hourlyValue = resolveHourlyValueRaw({
        hourRow,
        articleData,
        columnKey,
        baseContext
      });
      if (hourlyValue !== null) {
        const fraction = Math.max(0, Math.min(1, step / 60));
        total += resolveAzucarGlassUnits({ articleData, hourlyValue, baseContext }) * fraction;
      }
      usedMinutes += step;
    } else if (startedSegment) {
      break;
    }

    cursor = new Date(cursor.getTime() + step * 60000);
  }

  return total;
};

const buildHourlyDemandSummary = ({
  forecastData,
  articleName,
  simulationTime,
  hoursOverride,
  categoryAdjustments
}) => {
  const forecastAverages = forecastData?.forecastAverages || [];
  const selectedArticulos = forecastData?.selectedArticulos || [];
  if (!forecastAverages.length || !selectedArticulos.length || !articleName) {
    return { rows: [], totalExact: 0, totalRounded: 0 };
  }
  const articulo = getArticleData(selectedArticulos, articleName);
  if (!articulo) {
    return { rows: [], totalExact: 0, totalRounded: 0 };
  }

  const modifiers = sanitizeModifiers(forecastData.modificadores);
  const recipeProcess = isRecipeProcess(selectedArticulos);
  const categoryKey = buildCategoryKey(articulo, recipeProcess);
  const columnKey = detectColumnKey(articulo, articleName, forecastAverages);
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastAverages);
  const windowMinutes = getWindowMinutes(articulo, hoursOverride);
  const start = sanitizeDate(simulationTime);
  const horizonEnd = new Date(start.getTime() + windowMinutes * 60000);

  const rows = [];
  let totalExact = 0;
  let totalRounded = 0;

  for (let i = 0; i < Math.ceil(windowMinutes / 60); i++) {
    const blockStart = new Date(start.getTime() + i * 3600000);
    const blockEnd = new Date(Math.min(blockStart.getTime() + 3600000, horizonEnd.getTime()));
    if (blockStart >= horizonEnd) {
      break;
    }
    let rowDemand = 0;
    let cursor = new Date(blockStart);
    while (cursor < blockEnd) {
      const minutesToHourEnd = 60 - cursor.getMinutes();
      const remaining = Math.max(0, Math.ceil((blockEnd - cursor) / 60000));
      const step = Math.min(15, minutesToHourEnd, remaining);
      if (isArticleAvailableAtTime(articulo, cursor)) {
        const dayName = DAYS[cursor.getDay()];
        const hourRow = forecastAverages.find((row) => row.Dia === dayName && row.Hora === cursor.getHours());
        const hourlyValue = resolveHourlyValueAdjusted({
          hourRow,
          articleData: articulo,
          columnKey,
          categoryKey,
          modifiers,
          categoryAdjustments,
          baseContext
        });
        if (hourlyValue !== null) {
          const fraction = step / 60;
          rowDemand += resolveAzucarGlassUnits({ articleData: articulo, hourlyValue, baseContext }) * fraction;
        }
      }
      cursor = new Date(cursor.getTime() + step * 60000);
    }
    if (rowDemand > 0) {
      const roundedRowDemand = roundQuantityWithThreshold(rowDemand);
      totalExact += rowDemand;
      totalRounded += roundedRowDemand;
      rows.push({
        label: `${blockStart.toLocaleDateString('es-MX', { weekday: 'short' })}, ${blockStart.toTimeString().slice(0, 5)} - ${blockEnd.toTimeString().slice(0, 5)}`,
        value: rowDemand
      });
    }
  }

  return { rows, totalExact, totalRounded };
};

const formatStockoutTime = (totalMinutes) => {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return '0min';
  }
  if (totalMinutes < 60) {
    const minutes = Math.floor(totalMinutes);
    return `${minutes}min`;
  }
  if (totalMinutes < 1440) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }
  const roundedHours = roundHoursWithThreshold(totalMinutes / 60, FORECAST_ROUND_THRESHOLD, 1);
  const days = Math.floor(roundedHours / 24);
  const hours = roundedHours % 24;
  if (days >= 7) {
    return '+7 días';
  }
  if (hours > 0) {
    return `${days}d ${hours}h`;
  }
  return `${days}d`;
};

export const calculateStockoutTime = ({
  articleName,
  currentStock,
  forecastData,
  simulationTime,
  categoryAdjustments,
  modifiers
}) => {
  if (!forecastData || !forecastData.forecastAverages) {
    return { hours: 0, minutes: 0, totalMinutes: 0, status: 'warning', text: 'Captura tu stock', formattedTime: '0min' };
  }
  const selectedArticulos = forecastData.selectedArticulos || [];
  const articleData = getArticleData(selectedArticulos, articleName);
  if (!articleData) {
    return { hours: 0, minutes: 0, totalMinutes: 0, status: 'warning', text: 'Captura tu stock', formattedTime: '0min' };
  }
  if (currentStock <= 0 && (!forecastData.modificadores || !forecastData.forecastAverages.length)) {
    return { hours: 0, minutes: 0, totalMinutes: 0, status: 'warning', text: 'Captura tu stock', formattedTime: '0min' };
  }

  const recipeProcess = isRecipeProcess(selectedArticulos);
  const categoryKey = buildCategoryKey(articleData, recipeProcess);
  const columnKey = detectColumnKey(articleData, articleName, forecastData.forecastAverages);
  const sanitizedModifiers = sanitizeModifiers(modifiers || forecastData.modificadores);
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastData.forecastAverages);

  const start = sanitizeDate(simulationTime);
  let remainingStock = currentStock;
  let totalMinutesUntilStockout = 0;

  for (let totalMinutes = 0; totalMinutes < 168 * 60; totalMinutes += 15) {
    const futureTime = new Date(start.getTime() + totalMinutes * 60000);
    if (!isArticleAvailableAtTime(articleData, futureTime)) {
      continue;
    }
    const dayName = DAYS[futureTime.getDay()];
    const hourRow = forecastData.forecastAverages.find((row) => row.Dia === dayName && row.Hora === futureTime.getHours());
    const hourlyDemand = resolveHourlyValueAdjusted({
      hourRow,
      articleData,
      columnKey,
      categoryKey,
      modifiers: sanitizedModifiers,
      categoryAdjustments,
      baseContext
    });
    if (hourlyDemand === null) {
      continue;
    }
    const fifteenMinuteDemand = resolveAzucarGlassUnits({
      articleData,
      hourlyValue: hourlyDemand,
      baseContext
    }) / 4;
    remainingStock -= fifteenMinuteDemand;
    if (remainingStock <= 0) {
      const stockBefore = remainingStock + fifteenMinuteDemand;
      const consumptionRate = fifteenMinuteDemand / 15;
      const minutesUntilDepletion = consumptionRate > 0 ? stockBefore / consumptionRate : 0;
      totalMinutesUntilStockout = totalMinutes + Math.min(15, Math.ceil(minutesUntilDepletion));
      break;
    }
  }

  if (totalMinutesUntilStockout === 0) {
    totalMinutesUntilStockout = 168 * 60;
  }

  const hours = Math.floor(totalMinutesUntilStockout / 60);
  const minutes = Math.floor(totalMinutesUntilStockout % 60);
  const formattedTime = formatStockoutTime(totalMinutesUntilStockout);
  let status = 'ok';
  let text = '';

  if (totalMinutesUntilStockout <= 120) {
    status = 'critical';
    text = `Stock actual se agota en ${formattedTime}`;
  } else if (totalMinutesUntilStockout <= 360) {
    status = 'warning';
    text = `Stock actual se agota en ${formattedTime}`;
  } else {
    text = `Stock actual dura ${formattedTime}`;
  }

  return { hours, minutes, totalMinutes: totalMinutesUntilStockout, status, text, formattedTime };
};

const computeOperatingSegments = (articleData, windowMinutes, simulationTime) => {
  const segments = [];
  if (!articleData) {
    return segments;
  }
  const start = sanitizeDate(simulationTime);
  let cursor = new Date(start);
  let used = 0;
  let currentSegStart = null;
  const horizonEnd = new Date(start.getTime() + windowMinutes * 60000);

  while (used < windowMinutes && cursor < horizonEnd) {
    const minutesToHourEnd = 60 - cursor.getMinutes();
    const remaining = Math.max(0, Math.ceil((horizonEnd - cursor) / 60000));
    const step = Math.min(15, minutesToHourEnd, windowMinutes - used, remaining);
    if (isArticleAvailableAtTime(articleData, cursor)) {
      if (!currentSegStart) {
        currentSegStart = new Date(cursor);
      }
      used += step;
    } else if (currentSegStart) {
      segments.push({ start: currentSegStart, end: new Date(cursor) });
      currentSegStart = null;
    }
    cursor = new Date(cursor.getTime() + step * 60000);
  }
  if (currentSegStart) {
    segments.push({ start: currentSegStart, end: new Date(cursor) });
  }
  return segments;
};

const getOperatingSegmentsTooltip = (articleData, windowMinutes, simulationTime) => {
  const segments = computeOperatingSegments(articleData, windowMinutes, simulationTime);
  if (!segments.length) {
    return '';
  }
  const format = (date) => date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true });
  const day = (date) => date.toLocaleDateString('es-MX', { weekday: 'short' });
  const items = segments.map((segment) => `${day(segment.start)} ${format(segment.start)}–${format(segment.end)}`);
  return items.length > 4 ? `${items.slice(0, 4).join(', ')}, …` : items.join(', ');
};

const calculateProductionPeriod = (articleData, hoursOverride, simulationTime) => {
  const windowMinutes = getWindowMinutes(articleData, hoursOverride);
  const effectiveSegments = computeOperatingSegments(articleData, windowMinutes, simulationTime);
  const start = sanitizeDate(simulationTime);
  const end = effectiveSegments.length ? effectiveSegments[effectiveSegments.length - 1].end : new Date(start.getTime() + windowMinutes * 60000);
  const effectiveMinutes = effectiveSegments.reduce((sum, segment) => sum + Math.max(0, (segment.end - segment.start) / 60000), 0);
  const effectiveHours = roundHoursWithThreshold(effectiveMinutes / 60, FORECAST_ROUND_THRESHOLD, 1);

  // Detect when operating hours completely block the forecast
  if (effectiveMinutes <= 0 || effectiveHours <= 0) {
    return {
      isBlocked: true,
      rangeText: 'Horario limitado',
      hoursText: '',
      combined: 'Horario limitado'
    };
  }

  // Jonathan Ive style: clean, minimal, functional formatting
  const formatTime = (date) => {
    const time = date.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true });
    return time.replace(/\s/g, '').toLowerCase();
  };
  const formatDay = (date) => date.toLocaleDateString('es-MX', { weekday: 'short' });

  // Compact day display: only show second day if different
  const startDay = formatDay(start);
  const endDay = formatDay(end);
  const sameDay = startDay === endDay;

  const rangeDisplay = sameDay
    ? `${startDay} ${formatTime(start)} – ${formatTime(end)}`
    : `${startDay} ${formatTime(start)} – ${endDay} ${formatTime(end)}`;

  // Return structured object for separate display
  return {
    isBlocked: false,
    rangeText: rangeDisplay,
    hoursText: `${effectiveHours}h`,
    combined: `${rangeDisplay} · ${effectiveHours}h`
  };
};

const buildSummaryStats = (items) => {
  if (!items.length) {
    return {
      totalItems: 0,
      totalToProduce: 0,
      totalPrepMinutes: 0,
      stockCoverage: 100,
      criticalCount: 0,
      warningCount: 0,
      okCount: 0
    };
  }
  const criticalCount = items.filter((item) => item.status === 'critical').length;
  const warningCount = items.filter((item) => item.status === 'warning').length;
  const okCount = items.filter((item) => item.status === 'ok').length;
  const totalToProduce = items.reduce((sum, item) => sum + Math.max(0, item.quantity), 0);
  const totalPrepMinutes = items.reduce((sum, item) => sum + (item.prepMinutes || 0), 0);
  const stockCoverage = Math.round(((items.length - criticalCount) / items.length) * 100);
  return {
    totalItems: items.length,
    totalToProduce,
    totalPrepMinutes,
    stockCoverage,
    criticalCount,
    warningCount,
    okCount
  };
};

export const buildProductionSummary = (forecastData, {
  simulationTime,
  hoursOverride,
  stockOverrides,
  categoryAdjustments
}) => {
  if (!forecastData) {
    return null;
  }
  const selectedArticulos = forecastData.selectedArticulos || [];
  if (!selectedArticulos.length) {
    return null;
  }
  const azucarGlassOnly = selectedArticulos.every((articulo) => Boolean(articulo?.proceso_es_azucar_glass));
  const modifiers = sanitizeModifiers(forecastData.modificadores);
  const recipeProcess = isRecipeProcess(selectedArticulos);
  const forecastAverages = forecastData.forecastAverages || [];
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastAverages);

  const standardItems = selectedArticulos.map((articulo) => {
    const isAzucarGlass = Boolean(articulo.proceso_es_azucar_glass);
    const demandSummary = buildHourlyDemandSummary({
      forecastData,
      articleName: articulo.articulo,
      simulationTime,
      hoursOverride,
      categoryAdjustments
    });
    const categoryKey = buildCategoryKey(articulo, recipeProcess);
    const adjustedForecastExact = demandSummary.totalExact;
    const adjustedForecastRounded = demandSummary.totalRounded;
    const stock = Math.max(0, getStockValue(articulo, stockOverrides));
    const adjustedForecastBase = isAzucarGlass ? adjustedForecastExact : adjustedForecastRounded;
    const adjustedForecastDisplay = isAzucarGlass ? roundAzucarValue(adjustedForecastExact) : adjustedForecastRounded;
    const toProduceDecimal = Math.max(0, adjustedForecastBase - stock);
    const toProduceDisplay = isAzucarGlass
      ? roundAzucarValue(toProduceDecimal)
      : roundQuantityWithThreshold(toProduceDecimal);
    const stockoutInfo = calculateStockoutTime({
      articleName: articulo.articulo,
      currentStock: stock,
      forecastData,
      simulationTime,
      categoryAdjustments,
      modifiers
    });
    let status = 'warning';
    if (isAzucarGlass) {
      status = 'neutral';
    } else if (toProduceDecimal > adjustedForecastBase * 0.8) {
      status = 'critical';
    } else if (stock >= adjustedForecastBase) {
      status = 'ok';
    }

    // Get structured period data
    const periodData = calculateProductionPeriod(articulo, hoursOverride, simulationTime);

    return {
      name: articulo.articulo,
      displayName: articulo.unidades && articulo.unidades !== 'unidades' && articulo.unidades !== 'piezas'
        ? `${articulo.articulo} (${articulo.unidades})`
        : articulo.articulo,
      // Structured period information
      periodData,
      periodTooltip: getOperatingSegmentsTooltip(articulo, getWindowMinutes(articulo, hoursOverride), simulationTime),
      imagen_url: articulo.imagen_url,
      category: articulo.categoria || 'Sin categoría',
      linkedArticle: recipeProcess ? (articulo.articulo_nombre || 'Sin artículo vinculado') : null,
      quantity: toProduceDisplay,
      quantityExact: toProduceDecimal,
      status,
      isAzucarGlass,
      stockout: stockoutInfo,
      forecast: adjustedForecastExact,
      adjustedForecast: adjustedForecastDisplay,
      forecastExact: adjustedForecastExact,
      forecastRounded: adjustedForecastDisplay,
      forecastDisplay: isAzucarGlass ? adjustedForecastDisplay.toFixed(2) : adjustedForecastRounded,
      stock,
      stockDurationText: stockoutInfo?.text || '',
      // Legacy fields for backwards compatibility
      lifespan: periodData.combined,
      periodHtml: periodData.combined,
      lifespanMinutes: articulo.minutos || 1440,
      unitLabel: (articulo.unidades || 'UNIDADES').toUpperCase(),
      prepMinutes: articulo.tiempo_preparacion_min || 0,
      operatingRestricted: Array.isArray(articulo.horarios_operacion) && articulo.horarios_operacion.some((h) => !h.es_24_horas),
      operatingTooltip: getOperatingSegmentsTooltip(articulo, getWindowMinutes(articulo, hoursOverride), simulationTime)
    };
  });

  const items = (() => {
    if (!azucarGlassOnly || !baseContext?.baseArticle) {
      return standardItems;
    }
    const baseArticle = baseContext.baseArticle;
    const baseItem = standardItems.find((item) => item.name === baseArticle.articulo);
    if (!baseItem) {
      return standardItems;
    }
    const baseQuantityExact = baseItem.quantityExact;
    const recipeAmounts = splitAzucarGlassRecipes(baseQuantityExact);
    if (!recipeAmounts.length) {
      return standardItems;
    }
    const ingredientArticles = selectedArticulos.filter((articulo) => (
      articulo !== baseArticle && resolveAzucarGlassEquivalence(articulo)
    ));
    const ingredientNames = new Set(ingredientArticles.map((articulo) => articulo.articulo));
    const baseUnitLabel = (baseArticle.unidades || baseArticle.unidad || 'UNIDADES').toString().toUpperCase();

    const totalRecipes = recipeAmounts.length;
    const recipeItems = recipeAmounts.map((amount, index) => {
      const ingredients = buildAzucarGlassIngredients({
        selectedArticulos: ingredientArticles,
        baseArticle,
        recipeAmount: amount
      });
      const roundedAmount = roundAzucarValue(amount);
      const baseIngredient = {
        name: baseArticle.articulo,
        quantity: roundedAmount,
        displayQuantity: roundedAmount.toFixed(2),
        unitLabel: baseUnitLabel,
        isBase: true
      };
      return {
        name: `Receta #${index + 1}`,
        displayName: `Receta #${index + 1}`,
        baseName: baseArticle.articulo,
        periodData: baseItem.periodData,
        periodTooltip: baseItem.periodTooltip,
        imagen_url: baseItem.imagen_url,
        category: baseItem.category,
        linkedArticle: baseItem.linkedArticle,
        quantity: roundedAmount,
        quantityExact: amount,
        status: 'neutral',
        isAzucarGlass: true,
        stockout: null,
        forecast: roundedAmount,
        adjustedForecast: roundedAmount,
        forecastExact: amount,
        forecastRounded: roundedAmount,
        forecastDisplay: roundedAmount.toFixed(2),
        stock: 0,
        stockDurationText: '',
        lifespan: baseItem.lifespan,
        periodHtml: baseItem.periodHtml,
        lifespanMinutes: baseItem.lifespanMinutes,
        unitLabel: baseUnitLabel,
        prepMinutes: baseItem.prepMinutes,
        operatingRestricted: baseItem.operatingRestricted,
        operatingTooltip: baseItem.operatingTooltip,
        isRecipe: true,
        recipeIndex: index,
        totalRecipes,
        ingredients: [baseIngredient, ...ingredients]
      };
    });

    const remainingItems = standardItems.filter((item) => (
      item.name !== baseArticle.articulo && !ingredientNames.has(item.name)
    ));

    return [...recipeItems, ...remainingItems];
  })();

  const groups = (() => {
    const grouped = items.reduce((acc, item) => {
      const key = recipeProcess ? (item.linkedArticle || 'Sin artículo vinculado') : item.category;
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});
    return Object.keys(grouped).sort((a, b) => a.localeCompare(b)).map((name) => {
      const key = recipeProcess ? `linked_${name}` : `category_${name}`;
      const adjustment = categoryAdjustments?.[key];
      const groupItems = grouped[name];
      return {
        name,
        key,
        categoryType: recipeProcess ? 'linkedArticle' : 'category',
        adjustment: adjustment ? { percentage: adjustment.percentage } : null,
        adjustmentDetails: adjustment || null,
        items: grouped[name]
      };
    });
  })();

  const hoursLabel = clampHoursOverride(hoursOverride, azucarGlassOnly ? 24 : 12);
  const title = hoursLabel
    ? `Unidades a producir para próximas ${hoursLabel} horas${azucarGlassOnly ? '' : ' (máx. por vida útil)'}`
    : (azucarGlassOnly ? 'Unidades a producir por horas' : 'Unidades a producir de acuerdo a tiempo de vida');

  return {
    title,
    subtitle: 'Resumen visual de producción requerida',
    isRecipeProcess: recipeProcess,
    groupType: recipeProcess ? 'artículo vinculado' : 'categoría',
    groups,
    stats: buildSummaryStats(items)
  };
};

export const buildDetailedTable = (forecastData, {
  simulationTime,
  hoursOverride,
  categoryAdjustments
}) => {
  if (!forecastData) {
    return null;
  }
  const forecastAverages = forecastData.forecastAverages || [];
  const selectedArticulos = forecastData.selectedArticulos || [];
  if (!selectedArticulos.length) {
    return null;
  }
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastAverages);
  const headers = ['Hora', ...selectedArticulos.map((art) => art.articulo)];
  const maxVidaHoras = Math.max(...selectedArticulos.map((art) => (art.minutos || 0) / 60 + 1), 1);
  const azucarGlassOnly = selectedArticulos.every((articulo) => Boolean(articulo?.proceso_es_azucar_glass));
  const override = clampHoursOverride(hoursOverride, azucarGlassOnly ? 24 : 12);
  const maxHours = override ? override : Math.min(24, maxVidaHoras);
  const rows = [];

  for (let i = 0; i < maxHours; i++) {
    const startTime = new Date(sanitizeDate(simulationTime).getTime() + i * 3600000);
    const nextHour = new Date(startTime.getTime() + 3600000);
    const rowValues = [];
    headers.slice(1).forEach((articleName) => {
      const articulo = getArticleData(selectedArticulos, articleName);
      if (!articulo) {
        rowValues.push('');
        return;
      }
      const isAzucarGlass = Boolean(articulo.proceso_es_azucar_glass);
      const windowMinutes = getWindowMinutes(articulo, hoursOverride);
      const horizonEnd = new Date(sanitizeDate(simulationTime).getTime() + windowMinutes * 60000);
      const rowEnd = new Date(Math.min(nextHour.getTime(), horizonEnd.getTime()));
      if (startTime >= horizonEnd) {
        rowValues.push('');
        return;
      }
      let rowDemand = 0;
      const categoryKey = buildCategoryKey(articulo, isRecipeProcess(selectedArticulos));
      const columnKey = detectColumnKey(articulo, articleName, forecastAverages);
      let cursor = new Date(startTime);
      while (cursor < rowEnd) {
        const minutesToHourEnd = 60 - cursor.getMinutes();
        const remaining = Math.max(0, Math.ceil((rowEnd - cursor) / 60000));
        const step = Math.min(15, minutesToHourEnd, remaining);
        if (isArticleAvailableAtTime(articulo, cursor)) {
          const dayName = DAYS[cursor.getDay()];
          const hourRow = forecastAverages.find((row) => row.Dia === dayName && row.Hora === cursor.getHours());
          const hourlyValue = resolveHourlyValueAdjusted({
            hourRow,
            articleData: articulo,
            columnKey,
            categoryKey,
            modifiers: forecastData.modificadores,
            categoryAdjustments,
            baseContext
          });
          if (hourlyValue !== null) {
            const fraction = step / 60;
            rowDemand += resolveAzucarGlassUnits({ articleData: articulo, hourlyValue, baseContext }) * fraction;
          }
        }
        cursor = new Date(cursor.getTime() + step * 60000);
      }
      if (rowDemand > 0) {
        rowValues.push(isAzucarGlass ? rowDemand.toFixed(2) : roundQuantityWithThreshold(rowDemand));
      } else {
        rowValues.push('');
      }
    });
    rows.push({
      label: `${startTime.toLocaleDateString('es-MX', { weekday: 'short' })} ${startTime.toTimeString().slice(0, 5)}-${nextHour.toTimeString().slice(0, 5)}`,
      values: rowValues
    });
  }

  return {
    headers,
    rows,
    periodLabel: override ? `Próximas ${override} horas (respetando vida útil)` : 'Próximas 24 horas'
  };
};

export const buildHourlySalesData = (forecastData, {
  articleName,
  simulationTime,
  hoursOverride,
  categoryAdjustments
}) => {
  if (!forecastData || !articleName) {
    return null;
  }
  const articulo = getArticleData(forecastData?.selectedArticulos || [], articleName);
  const summary = buildHourlyDemandSummary({
    forecastData,
    articleName,
    simulationTime,
    hoursOverride,
    categoryAdjustments
  });

  const totalRounded = Number.isFinite(summary.totalRounded) ? summary.totalRounded : 0;
  const unitLabel = articulo?.unidades || articulo?.unidad || 'unidades';
  const isAzucarGlass = Boolean(articulo?.proceso_es_azucar_glass);

  return {
    title: `Ventas por Hora - ${articleName}`,
    articleName,
    total: totalRounded,
    totalDisplay: totalRounded.toFixed(2),
    unitLabel,
    isAzucarGlass,
    rows: summary.rows.map((row) => ({
      label: row.label,
      value: row.value,
      displayValue: row.value.toFixed(2)
    }))
  };
};

export const buildForecastCsvRows = (forecastData, {
  simulationTime,
  hoursOverride,
  categoryAdjustments,
  restaurantName
} = {}) => {
  if (!forecastData) {
    return [];
  }
  const selectedArticulos = forecastData.selectedArticulos || [];
  const forecastAverages = forecastData.forecastAverages || [];
  if (!selectedArticulos.length || !forecastAverages.length) {
    return [];
  }
  const modifiers = sanitizeModifiers(forecastData.modificadores);
  const baseContext = getAzucarGlassBaseContext(selectedArticulos, forecastAverages);
  const recipeProcess = isRecipeProcess(selectedArticulos);
  const start = sanitizeDate(simulationTime);
  const restaurantLabel = restaurantName || '';
  const rows = [];

  selectedArticulos.forEach((articulo) => {
    if (!articulo?.articulo) {
      return;
    }
    const categoryKey = buildCategoryKey(articulo, recipeProcess);
    const columnKey = detectColumnKey(articulo, articulo.articulo, forecastAverages);
    const windowMinutes = getWindowMinutes(articulo, hoursOverride);
    const horizonEnd = new Date(start.getTime() + windowMinutes * 60000);

    for (let offset = 0; offset < windowMinutes; offset += 60) {
      const blockStart = new Date(start.getTime() + offset * 60000);
      const blockEnd = new Date(Math.min(blockStart.getTime() + 3600000, horizonEnd.getTime()));
      if (blockStart >= horizonEnd) {
        break;
      }

      let blockDemand = 0;
      let cursor = new Date(blockStart);
      while (cursor < blockEnd) {
        const minutesToHourEnd = 60 - cursor.getMinutes();
        const remaining = Math.max(0, Math.ceil((blockEnd - cursor) / 60000));
        const step = Math.min(15, minutesToHourEnd, remaining);

        if (isArticleAvailableAtTime(articulo, cursor)) {
          const dayName = DAYS[cursor.getDay()];
          const hourRow = forecastAverages.find((row) => row.Dia === dayName && row.Hora === cursor.getHours());
          const hourlyValue = resolveHourlyValueAdjusted({
            hourRow,
            articleData: articulo,
            columnKey,
            categoryKey,
            modifiers,
            categoryAdjustments,
            baseContext
          });
          if (hourlyValue !== null) {
            const fraction = step / 60;
            blockDemand += resolveAzucarGlassUnits({ articleData: articulo, hourlyValue, baseContext }) * fraction;
          }
        }
        cursor = new Date(cursor.getTime() + step * 60000);
      }

      if (blockDemand > 0) {
        rows.push({
          restaurantName: restaurantLabel,
          itemType: recipeProcess ? 'Receta' : 'Articulo',
          itemName: articulo.articulo,
          day: DAYS[blockStart.getDay()],
          startTime: blockStart.toTimeString().slice(0, 5),
          endTime: blockEnd.toTimeString().slice(0, 5),
          quantity: Number(blockDemand.toFixed(1)),
          unit: (articulo.unidades || articulo.unidad || 'UNIDADES').toString()
        });
      }
    }
  });

  return rows;
};

export const buildForecastSavePayload = ({
  forecastData,
  queryParams,
  stockOverrides,
  categoryAdjustments,
  hoursOverride,
  simulationTime,
  snapshotUuid
}) => {
  if (!forecastData || !queryParams) {
    return null;
  }
  const selectedArticulos = forecastData.selectedArticulos || [];
  if (!selectedArticulos.length) {
    return null;
  }
  const modifiers = sanitizeModifiers(forecastData.modificadores);
  const recipeProcess = isRecipeProcess(selectedArticulos);
  const localStart = sanitizeDate(simulationTime);
  const details = selectedArticulos.map((articulo) => {
    const entidadId = recipeProcess
      ? (articulo.receta_id || articulo.id || null)
      : (articulo.articulo_id || articulo.id || null);
    const demandSummary = buildHourlyDemandSummary({
      forecastData,
      articleName: articulo.articulo,
      simulationTime,
      hoursOverride,
      categoryAdjustments
    });
    const categoryKey = buildCategoryKey(articulo, recipeProcess);
    const adjustedForecastExact = demandSummary.totalExact;
    const adjustedForecastRounded = demandSummary.totalRounded;
    const stock = Math.max(0, getStockValue(articulo, stockOverrides));
    const isAzucarGlass = Boolean(articulo?.proceso_es_azucar_glass);
    const adjustedForecastValue = isAzucarGlass ? roundAzucarValue(adjustedForecastExact) : adjustedForecastRounded;
    const toProduceDecimal = Math.max(0, adjustedForecastValue - stock);
    const toProduceValue = isAzucarGlass ? roundAzucarValue(toProduceDecimal) : roundQuantityWithThreshold(toProduceDecimal);
    const coverageMinutes = Math.min(articulo.minutos || 1440, getWindowMinutes(articulo, hoursOverride));
    const coverageEnd = new Date(localStart.getTime() + coverageMinutes * 60000);
    const adjustmentsPayload = Array.isArray(categoryAdjustments?.[categoryKey]?.adjustmentsByMotivo)
      ? categoryAdjustments[categoryKey].adjustmentsByMotivo.map((item) => ({
        motivoId: item.motivoId,
        motivoNombre: item.motivoNombre,
        value: item.value
      }))
      : [];

    return {
      entidad_id: entidadId,
      entidad_tipo: recipeProcess ? 'receta' : 'articulo',
      entidad_nombre: articulo.articulo,
      dia: DAYS[localStart.getDay()],
      hora: localStart.getHours(),
      cantidad_pronosticada: adjustedForecastValue,
      stock_capturado: stock,
      cantidad_ajustada: adjustedForecastValue,
      cantidad_a_producir: toProduceValue,
      hora_inicial_cobertura: formatCoverageTimestamp(localStart),
      hora_final_cobertura: formatCoverageTimestamp(coverageEnd),
      ajustes: adjustmentsPayload
    };
  });

  const operatingHoursSnapshot = selectedArticulos.map((articulo) => ({
    entidad_id: recipeProcess
      ? (articulo.receta_id || articulo.id || null)
      : (articulo.articulo_id || articulo.id || null),
    entidad_tipo: recipeProcess ? 'receta' : 'articulo',
    entidad_nombre: articulo.articulo,
    minutos: articulo.minutos || 1440,
    horarios_operacion: Array.isArray(articulo.horarios_operacion) ? articulo.horarios_operacion : []
  }));

  const clonedParams = JSON.parse(JSON.stringify(queryParams));
  delete clonedParams.timezone_offset;
  const parametrosJson = {
    ...clonedParams,
    operating_hours_applied: true,
    operating_hours_snapshot: operatingHoursSnapshot,
    calculation_minutes_step: 15,
    version: clonedParams?.version || 'v1-oper-hours'
  };

  return {
    snapshot_uuid: snapshotUuid,
    sucursal_nombre: queryParams.sucursal,
    parametros_json: parametrosJson,
    comentarios: '',
    details
  };
};
